import {
	DeleteOutlined,
	NotificationOutlined,
	PlusOutlined,
} from '@ant-design/icons'
import {
	Alert,
	Button,
	Card,
	Checkbox,
	Col,
	Collapse,
	DatePicker,
	Divider,
	Form,
	Input,
	Radio,
	Row,
	Select,
	Space,
	Tabs,
	Tag,
	message,
} from 'antd'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ListPageIntro, ListPageShell } from '../../common'
import api from '../../api/index.api'
import {
	ANNOUNCEMENT_PRIORITY,
	ANNOUNCEMENT_STATUS,
	ANNOUNCEMENT_SURFACE,
	emptyAnnouncementDraft,
	emptySlide,
	isValidDeepLink,
	isValidHttpUrl,
	slugify,
} from '../../services/announcementMock.store'
import { ROUTE } from '../../utils/constant'
import './AnnouncementCreate.scss'

const { TabPane } = Tabs
const { Button: RadioButton } = Radio

const MAX_SLIDES = 3
const MAX_TITLE = 120
const MAX_BODY = 500
const MAX_CTA = 40

const listPath = `${ROUTE.ANNOUNCEMENT_MANAGEMENT}${ROUTE.LIST}`

const PLATFORM_OPTIONS = [
	{ label: 'Web', value: 'web' },
	{ label: 'iOS', value: 'ios' },
	{ label: 'Android', value: 'android' },
]

const SURFACE_OPTIONS = [
	{
		label: 'What’s New popup',
		value: ANNOUNCEMENT_SURFACE.MODAL_CAROUSEL,
	},
	{ label: 'Home banner', value: ANNOUNCEMENT_SURFACE.HOME_BANNER },
	{ label: 'Help → What’s new', value: ANNOUNCEMENT_SURFACE.CHANGELOG },
]

const toMoment = value => (value ? moment(value) : null)

const fromMoment = value =>
	value && moment.isMoment(value) ? value.toISOString() : null

const announcementToFormValues = item => ({
	slug: item.slug,
	status: item.status,
	priority: item.priority,
	platforms: item.platforms || [],
	surfaces: item.surfaces || [],
	startsAt: toMoment(item.startsAt),
	endsAt: toMoment(item.endsAt),
	ctaDeepLink: item.ctaDeepLink || '',
	locales: {
		vi: {
			title: item.locales?.vi?.title || '',
			body: item.locales?.vi?.body || '',
			ctaLabel: item.locales?.vi?.ctaLabel || '',
		},
		en: {
			title: item.locales?.en?.title || '',
			body: item.locales?.en?.body || '',
			ctaLabel: item.locales?.en?.ctaLabel || '',
		},
	},
	slides:
		Array.isArray(item.slides) && item.slides.length > 0
			? item.slides.map(slide => ({
					imageUrl: slide.imageUrl || '',
					locales: {
						vi: {
							title: slide.locales?.vi?.title || '',
							body: slide.locales?.vi?.body || '',
						},
						en: {
							title: slide.locales?.en?.title || '',
							body: slide.locales?.en?.body || '',
						},
					},
			  }))
			: [emptySlide()],
})

const formValuesToPayload = values => ({
	slug: slugify(values.slug),
	status: values.status,
	priority: values.priority,
	platforms: values.platforms || [],
	surfaces: values.surfaces || [],
	startsAt: fromMoment(values.startsAt),
	endsAt: fromMoment(values.endsAt),
	ctaDeepLink: String(values.ctaDeepLink || '').trim(),
	locales: {
		vi: {
			title: String(values.locales?.vi?.title || '').trim(),
			body: String(values.locales?.vi?.body || '').trim(),
			ctaLabel: String(values.locales?.vi?.ctaLabel || '').trim(),
		},
		en: {
			title: String(values.locales?.en?.title || '').trim(),
			body: String(values.locales?.en?.body || '').trim(),
			ctaLabel: String(values.locales?.en?.ctaLabel || '').trim(),
		},
	},
	slides: (values.slides || []).map(slide => ({
		imageUrl: String(slide?.imageUrl || '').trim(),
		locales: {
			vi: {
				title: String(slide?.locales?.vi?.title || '').trim(),
				body: String(slide?.locales?.vi?.body || '').trim(),
			},
			en: {
				title: String(slide?.locales?.en?.title || '').trim(),
				body: String(slide?.locales?.en?.body || '').trim(),
			},
		},
	})),
})

const bannerHelpSectionTitle = surfaces => {
	const banner = surfaces.includes(ANNOUNCEMENT_SURFACE.HOME_BANNER)
	const help = surfaces.includes(ANNOUNCEMENT_SURFACE.CHANGELOG)
	if (banner && help) return 'Home banner & Help text'
	if (banner) return 'Home banner text'
	if (help) return 'Help → What’s new text'
	return 'Text'
}

export const AnnouncementCreate = () => {
	const history = useHistory()
	const location = useLocation()
	const [form] = Form.useForm()
	const [saving, setSaving] = useState(false)
	const [loading, setLoading] = useState(false)
	const [showReviewHint, setShowReviewHint] = useState(true)
	const [previewLocale, setPreviewLocale] = useState('vi')
	const [previewSlide, setPreviewSlide] = useState(0)
	const [imageBroken, setImageBroken] = useState(false)
	const [formSnapshot, setFormSnapshot] = useState(() =>
		announcementToFormValues(emptyAnnouncementDraft())
	)

	/** Keep preview in sync with full form store (incl. fields in inactive tabs). */
	const syncSnapshotFromForm = () => {
		setFormSnapshot(form.getFieldsValue(true))
	}

	const announcementId = useMemo(() => {
		const params = new URLSearchParams(location.search)
		const raw = params.get('id')
		const n = Number(raw)
		return Number.isFinite(n) && n > 0 ? n : null
	}, [location.search])

	const isEdit = announcementId != null

	useEffect(() => {
		if (!announcementId) {
			const draft = announcementToFormValues(emptyAnnouncementDraft())
			form.setFieldsValue(draft)
			setFormSnapshot(draft)
			setPreviewSlide(0)
			return undefined
		}
		let cancelled = false
		;(async () => {
			setLoading(true)
			try {
				const item = await api.announcementAdminApi.getById(announcementId)
				if (cancelled) return
				const values = announcementToFormValues(item)
				form.setFieldsValue(values)
				setFormSnapshot(values)
				setPreviewSlide(0)
			} catch (err) {
				message.error(err?.message || 'Could not load announcement.')
				history.replace(listPath)
			} finally {
				if (!cancelled) setLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [announcementId, form, history])

	const surfaces = formSnapshot.surfaces || []
	const status = formSnapshot.status || ANNOUNCEMENT_STATUS.DRAFT
	const needsModalSlides = surfaces.includes(
		ANNOUNCEMENT_SURFACE.MODAL_CAROUSEL
	)
	const needsHomeBanner = surfaces.includes(ANNOUNCEMENT_SURFACE.HOME_BANNER)
	const needsHelp = surfaces.includes(ANNOUNCEMENT_SURFACE.CHANGELOG)
	const needsBannerOrHelp = needsHomeBanner || needsHelp
	const needsAnyContent = needsBannerOrHelp || needsModalSlides
	const isScheduled = status === ANNOUNCEMENT_STATUS.SCHEDULED

	const preview = useMemo(() => {
		const locales = formSnapshot.locales || {}
		const locale = locales[previewLocale] || {}
		const slides = formSnapshot.slides || []
		const slide = slides[previewSlide] || slides[0] || emptySlide()
		const slideLocale = slide.locales?.[previewLocale] || {}
		return {
			title: locale.title || '—',
			body: locale.body || '—',
			ctaLabel: locale.ctaLabel || 'Learn more',
			ctaDeepLink: formSnapshot.ctaDeepLink || '',
			slideTitle: slideLocale.title || '—',
			slideBody: slideLocale.body || '—',
			slideImage: slide.imageUrl || '',
			slideCount: slides.length,
		}
	}, [formSnapshot, previewLocale, previewSlide])

	useEffect(() => {
		setImageBroken(false)
	}, [preview.slideImage, previewSlide])

	const onSlugBlur = () => {
		const slug = form.getFieldValue('slug')
		if (slug) {
			form.setFieldsValue({ slug: slugify(slug) })
		}
	}

	const onTitleBlurSuggestSlug = localeKey => {
		const currentSlug = form.getFieldValue('slug')
		if (currentSlug) return
		const title = form.getFieldValue(['locales', localeKey, 'title'])
		if (title) {
			const next = slugify(title)
			form.setFieldsValue({ slug: next })
			syncSnapshotFromForm()
		}
	}

	const validatePublishingFields = values => {
		const publishStatus = values.status
		const isPublish =
			publishStatus === ANNOUNCEMENT_STATUS.LIVE ||
			publishStatus === ANNOUNCEMENT_STATUS.SCHEDULED

		if (!isPublish) {
			return null
		}

		if (!(values.platforms || []).length) {
			return 'Select at least one platform before publishing.'
		}

		const surfacesSelected = values.surfaces || []
		if (!surfacesSelected.length) {
			return 'Select where to show before publishing.'
		}

		const hasBannerOrHelp =
			surfacesSelected.includes(ANNOUNCEMENT_SURFACE.HOME_BANNER) ||
			surfacesSelected.includes(ANNOUNCEMENT_SURFACE.CHANGELOG)
		const hasModal = surfacesSelected.includes(
			ANNOUNCEMENT_SURFACE.MODAL_CAROUSEL
		)

		if (hasBannerOrHelp) {
			const viTitle = String(values.locales?.vi?.title || '').trim()
			const enTitle = String(values.locales?.en?.title || '').trim()
			if (!viTitle || !enTitle) {
				return 'VI and EN titles are required for Home banner / Help.'
			}
		}

		const cta = String(values.ctaDeepLink || '').trim()
		if (!cta || !isValidDeepLink(cta)) {
			return 'Button link is required (app path /… or https://…).'
		}

		const viCta = String(values.locales?.vi?.ctaLabel || '').trim()
		const enCta = String(values.locales?.en?.ctaLabel || '').trim()
		if (!viCta || !enCta) {
			return 'VI and EN button labels are required when publishing.'
		}

		const startsAt = fromMoment(values.startsAt)
		const endsAt = fromMoment(values.endsAt)

		if (publishStatus === ANNOUNCEMENT_STATUS.SCHEDULED && !startsAt) {
			return 'Scheduled announcements require a start time.'
		}
		if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) {
			return 'End time must be after start time.'
		}

		if (hasModal) {
			const slides = values.slides || []
			if (!slides.length || slides.length > MAX_SLIDES) {
				return `What’s New popup needs 1–${MAX_SLIDES} slides.`
			}
			for (let i = 0; i < slides.length; i += 1) {
				const slide = slides[i]
				const imageUrl = String(slide?.imageUrl || '').trim()
				if (!imageUrl || !isValidHttpUrl(imageUrl)) {
					return `Slide ${i + 1}: image URL must be a valid http(s) link.`
				}
				const vi = String(slide?.locales?.vi?.title || '').trim()
				const en = String(slide?.locales?.en?.title || '').trim()
				if (!vi || !en) {
					return `Slide ${i + 1}: VI and EN titles are required.`
				}
			}
		}

		return null
	}

	const handleSubmit = async values => {
		const publishError = validatePublishingFields(values)
		if (publishError) {
			message.error(publishError)
			return
		}

		const payload = formValuesToPayload(values)
		if (!payload.slug) {
			const fromTitle =
				slugify(payload.locales?.en?.title) ||
				slugify(payload.locales?.vi?.title) ||
				`announcement-${Date.now()}`
			payload.slug = fromTitle
		}

		setSaving(true)
		try {
			if (isEdit) {
				await api.announcementAdminApi.update(announcementId, payload)
				message.success('Announcement updated.')
			} else {
				await api.announcementAdminApi.create(payload)
				message.success('Announcement created.')
			}
			history.push(listPath)
		} catch (err) {
			message.error(err?.message || 'Could not save announcement.')
		} finally {
			setSaving(false)
		}
	}

	const renderButtonLabelFields = localeKey => (
		<Form.Item
			label="Button label"
			name={['locales', localeKey, 'ctaLabel']}
			rules={[
				{
					max: MAX_CTA,
					message: `Max ${MAX_CTA} characters`,
				},
			]}
		>
			<Input
				placeholder={localeKey === 'vi' ? 'Vào học ngay' : 'Start learning'}
				maxLength={MAX_CTA}
				showCount
			/>
		</Form.Item>
	)

	const renderBannerHelpFields = localeKey => (
		<>
			<Form.Item
				label="Title"
				name={['locales', localeKey, 'title']}
				rules={[
					{
						max: MAX_TITLE,
						message: `Max ${MAX_TITLE} characters`,
					},
				]}
			>
				<Input
					placeholder={
						localeKey === 'vi'
							? 'Khóa học IELTS mới đã mở'
							: 'New IELTS course is live'
					}
					maxLength={MAX_TITLE}
					showCount
					onBlur={() => onTitleBlurSuggestSlug(localeKey)}
				/>
			</Form.Item>
			<Form.Item
				label="Short text"
				name={['locales', localeKey, 'body']}
				rules={[
					{
						max: MAX_BODY,
						message: `Max ${MAX_BODY} characters`,
					},
				]}
			>
				<Input.TextArea
					rows={3}
					placeholder="One or two sentences"
					maxLength={MAX_BODY}
					showCount
				/>
			</Form.Item>
			{renderButtonLabelFields(localeKey)}
		</>
	)

	return (
		<ListPageShell className="announcement-create">
			<ListPageIntro
				icon={NotificationOutlined}
				title={isEdit ? 'Edit announcement' : 'Create announcement'}
				subtitle="1) Choose platforms & where to show → 2) Fill matching content → 3) Set schedule & save."
			/>

			{showReviewHint && (
				<Alert
					className="announcement-create__alert"
					type="info"
					showIcon
					closable
					onClose={() => setShowReviewHint(false)}
					message="Saved to server. Run DB migration 0124 if create/list fails."
				/>
			)}

			<Row gutter={[24, 24]}>
				<Col xs={24} xl={14}>
					<Card loading={loading} bordered className="announcement-create__card">
						<Form
							form={form}
							layout="vertical"
							onFinish={handleSubmit}
							scrollToFirstError
							preserve
							onValuesChange={() => {
								const all = form.getFieldsValue(true)
								setFormSnapshot(all)
								const slides = all.slides || []
								if (previewSlide >= slides.length) {
									setPreviewSlide(Math.max(0, slides.length - 1))
								}
							}}
							initialValues={announcementToFormValues(
								emptyAnnouncementDraft()
							)}
						>
							{/* 1. Audience */}
							<div className="announcement-create__step">
								<div className="announcement-create__step-title">
									1. Who & where
								</div>
								<Form.Item
									label="Platforms"
									name="platforms"
									rules={[
										{
											validator: async (_, value) => {
												if (!value || !value.length) {
													throw new Error('Select at least one platform')
												}
											},
										},
									]}
								>
									<Checkbox.Group options={PLATFORM_OPTIONS} />
								</Form.Item>

								<Form.Item
									label="Where to show"
									name="surfaces"
									extra="Content fields below appear only for what you tick."
								>
									<Checkbox.Group
										className="announcement-create__surfaces"
										options={SURFACE_OPTIONS}
									/>
								</Form.Item>
							</div>

							{/* 2. Content */}
							<div className="announcement-create__step">
								<div className="announcement-create__step-title">
									2. Content
								</div>

								{!needsAnyContent && (
									<div className="announcement-create__empty-content">
										Tick at least one option in <strong>Where to show</strong>{' '}
										to edit text, slides, and the button link.
									</div>
								)}

								{needsAnyContent && (
									<Form.Item
										label="Button link"
										name="ctaDeepLink"
										rules={[
											{
												validator: async (_, value) => {
													const trimmed = String(value || '').trim()
													if (!trimmed) return
													if (!isValidDeepLink(trimmed)) {
														throw new Error(
															'Use /app-path or https://… URL'
														)
													}
												},
											},
										]}
										extra="Opens when users tap the button. One link for all platforms for now."
									>
										<Input placeholder="/courses/ielts-starter" />
									</Form.Item>
								)}

								{/* Popup first (matches preview priority), then banner/Help */}
								{needsModalSlides && (
									<section className="announcement-create__section">
										<Divider orientation="left" plain>
											What’s New popup
										</Divider>
										<p className="announcement-create__section-hint">
											1–{MAX_SLIDES} slides. Image URL + title (VI/EN) per
											slide.
										</p>

										{!needsBannerOrHelp && (
											<>
												<p className="announcement-create__section-hint">
													Button labels (shown on the popup):
												</p>
												<Tabs
													activeKey={previewLocale}
													onChange={key => setPreviewLocale(key)}
													size="small"
												>
													<TabPane tab="VI" key="vi" forceRender>
														{renderButtonLabelFields('vi')}
													</TabPane>
													<TabPane tab="EN" key="en" forceRender>
														{renderButtonLabelFields('en')}
													</TabPane>
												</Tabs>
											</>
										)}

										<Form.List name="slides">
											{(fields, { add, remove }) => (
												<>
													{fields.map((field, index) => (
														<Card
															key={field.key}
															size="small"
															className="announcement-create__slide"
															title={`Slide ${index + 1}`}
															extra={
																fields.length > 1 ? (
																	<Button
																		type="text"
																		danger
																		icon={<DeleteOutlined />}
																		onClick={() => remove(field.name)}
																	/>
																) : null
															}
														>
															<Form.Item
																label="Image URL"
																name={[field.name, 'imageUrl']}
																rules={[
																	{
																		validator: async (_, value) => {
																			const trimmed = String(
																				value || ''
																			).trim()
																			if (!trimmed) return
																			if (!isValidHttpUrl(trimmed)) {
																				throw new Error(
																					'Must be http(s) URL'
																				)
																			}
																		},
																	},
																]}
																extra="Paste image/GIF URL (upload later)."
															>
																<Input placeholder="https://…/feature.gif" />
															</Form.Item>
															<Tabs size="small" defaultActiveKey="vi">
																<TabPane tab="VI" key="vi" forceRender>
																	<Form.Item
																		label="Title"
																		name={[
																			field.name,
																			'locales',
																			'vi',
																			'title',
																		]}
																	>
																		<Input maxLength={MAX_TITLE} />
																	</Form.Item>
																	<Form.Item
																		label="Text"
																		name={[
																			field.name,
																			'locales',
																			'vi',
																			'body',
																		]}
																	>
																		<Input.TextArea
																			rows={2}
																			maxLength={MAX_BODY}
																		/>
																	</Form.Item>
																</TabPane>
																<TabPane tab="EN" key="en" forceRender>
																	<Form.Item
																		label="Title"
																		name={[
																			field.name,
																			'locales',
																			'en',
																			'title',
																		]}
																	>
																		<Input maxLength={MAX_TITLE} />
																	</Form.Item>
																	<Form.Item
																		label="Text"
																		name={[
																			field.name,
																			'locales',
																			'en',
																			'body',
																		]}
																	>
																		<Input.TextArea
																			rows={2}
																			maxLength={MAX_BODY}
																		/>
																	</Form.Item>
																</TabPane>
															</Tabs>
														</Card>
													))}
													{fields.length < MAX_SLIDES && (
														<Button
															type="dashed"
															block
															icon={<PlusOutlined />}
															onClick={() => add(emptySlide())}
															style={{ marginBottom: 8 }}
														>
															Add slide
														</Button>
													)}
												</>
											)}
										</Form.List>
									</section>
								)}

								{needsBannerOrHelp && (
									<section className="announcement-create__section">
										<Divider orientation="left" plain>
											{bannerHelpSectionTitle(surfaces)}
										</Divider>
										{needsModalSlides && (
											<p className="announcement-create__section-hint">
												Button label below is shared with the What’s New
												popup.
											</p>
										)}
										<Tabs
											activeKey={previewLocale}
											onChange={key => setPreviewLocale(key)}
										>
											<TabPane tab="Vietnamese" key="vi" forceRender>
												{renderBannerHelpFields('vi')}
											</TabPane>
											<TabPane tab="English" key="en" forceRender>
												{renderBannerHelpFields('en')}
											</TabPane>
										</Tabs>
									</section>
								)}
							</div>

							{/* 3. Schedule & publish */}
							<div className="announcement-create__step">
								<div className="announcement-create__step-title">
									3. Schedule & publish
								</div>
								<Row gutter={16}>
									<Col xs={24} sm={12}>
										<Form.Item
											label="Status"
											name="status"
											rules={[{ required: true }]}
										>
											<Select
												options={[
													{ value: 'draft', label: 'Draft' },
													{ value: 'scheduled', label: 'Scheduled' },
													{ value: 'live', label: 'Live' },
													{ value: 'archived', label: 'Archived' },
												]}
											/>
										</Form.Item>
									</Col>
									<Col xs={24} sm={12}>
										<Form.Item
											label="Priority"
											name="priority"
											rules={[{ required: true }]}
											extra="Critical can interrupt before other popups."
										>
											<Select
												options={[
													{
														value: ANNOUNCEMENT_PRIORITY.CRITICAL,
														label: 'Critical',
													},
													{
														value: ANNOUNCEMENT_PRIORITY.FEATURE,
														label: 'Feature',
													},
													{
														value: ANNOUNCEMENT_PRIORITY.TIP,
														label: 'Tip',
													},
												]}
											/>
										</Form.Item>
									</Col>
									<Col xs={24} sm={12}>
										<Form.Item
											label="Starts at"
											name="startsAt"
											rules={
												isScheduled
													? [
															{
																required: true,
																message: 'Required for Scheduled',
															},
													  ]
													: undefined
											}
											extra={
												isScheduled
													? 'Required for Scheduled.'
													: 'Optional for Live / Draft.'
											}
										>
											<DatePicker
												showTime
												style={{ width: '100%' }}
												format="YYYY-MM-DD HH:mm"
											/>
										</Form.Item>
									</Col>
									<Col xs={24} sm={12}>
										<Form.Item
											label="Ends at"
											name="endsAt"
											extra="Optional. Must be after start."
										>
											<DatePicker
												showTime
												style={{ width: '100%' }}
												format="YYYY-MM-DD HH:mm"
											/>
										</Form.Item>
									</Col>
								</Row>
							</div>

							{/* 4. Advanced */}
							<div className="announcement-create__step">
								<Collapse
									ghost
									className="announcement-create__advanced"
									expandIconPosition="right"
								>
									<Collapse.Panel
										header={
											<span className="announcement-create__step-title announcement-create__step-title--inline">
												4. Advanced
											</span>
										}
										key="advanced"
									>
										<Form.Item
											label="Slug"
											name="slug"
											rules={[
												{
													pattern: /^$|^[a-z0-9]+(?:-[a-z0-9]+)*$/,
													message: 'Lowercase kebab-case (a-z, 0-9, -)',
												},
											]}
											extra="Optional. Auto-filled from title if empty. Used for analytics & “seen” tracking."
										>
											<Input
												placeholder="ielts-course-launch"
												onBlur={onSlugBlur}
												maxLength={80}
											/>
										</Form.Item>
									</Collapse.Panel>
								</Collapse>
							</div>

							<div className="announcement-create__actions">
								<Space>
									<Button
										type="primary"
										htmlType="submit"
										loading={saving}
									>
										{isEdit ? 'Save changes' : 'Create announcement'}
									</Button>
									<Button
										onClick={() => history.push(listPath)}
										disabled={saving}
									>
										Cancel
									</Button>
								</Space>
							</div>
						</Form>
					</Card>
				</Col>

				<Col xs={24} xl={10}>
					<Card
						className="announcement-create__preview-card"
						title="Live preview"
						extra={
							<Radio.Group
								size="small"
								value={previewLocale}
								onChange={e => setPreviewLocale(e.target.value)}
								buttonStyle="solid"
							>
								<RadioButton value="vi">VI</RadioButton>
								<RadioButton value="en">EN</RadioButton>
							</Radio.Group>
						}
					>
						<div className="announcement-create__meta">
							<Tag>{status}</Tag>
							<Tag color="blue">{formSnapshot.priority || 'feature'}</Tag>
							{(formSnapshot.platforms || []).map(p => (
								<Tag key={p}>{p}</Tag>
							))}
							{needsModalSlides && <Tag color="purple">popup</Tag>}
							{needsHomeBanner && <Tag color="cyan">banner</Tag>}
							{needsHelp && <Tag color="geekblue">help</Tag>}
						</div>

						{!needsAnyContent && (
							<div className="announcement-create__preview-empty">
								Pick “Where to show” to preview.
							</div>
						)}

						{/* Interrupt first, then soft surfaces */}
						{needsModalSlides && (
							<section className="announcement-create__preview-block">
								<div className="announcement-create__preview-label">
									What’s New popup
									{preview.slideCount > 1 && (
										<span className="announcement-create__slide-nav">
											{[...Array(preview.slideCount)].map((_, i) => (
												<button
													key={i}
													type="button"
													className={
														i === previewSlide ? 'is-active' : undefined
													}
													onClick={() => setPreviewSlide(i)}
													aria-label={`Slide ${i + 1}`}
												/>
											))}
										</span>
									)}
								</div>
								<div className="announcement-create__modal">
									{preview.slideImage && !imageBroken ? (
										<img
											key={preview.slideImage}
											src={preview.slideImage}
											alt=""
											className="announcement-create__modal-img"
											onError={() => setImageBroken(true)}
										/>
									) : (
										<div className="announcement-create__modal-placeholder">
											{preview.slideImage
												? 'Image failed to load'
												: 'Image preview'}
										</div>
									)}
									<h3>{preview.slideTitle}</h3>
									<p>{preview.slideBody}</p>
									<button type="button" className="announcement-create__cta">
										{preview.ctaLabel}
									</button>
								</div>
							</section>
						)}

						{needsHomeBanner && (
							<section className="announcement-create__preview-block">
								<div className="announcement-create__preview-label">
									Home banner
								</div>
								<div className="announcement-create__banner">
									<div className="announcement-create__banner-text">
										<strong>{preview.title}</strong>
										<span>{preview.body}</span>
									</div>
									<button type="button" className="announcement-create__cta">
										{preview.ctaLabel}
									</button>
								</div>
							</section>
						)}

						{needsHelp && (
							<section className="announcement-create__preview-block">
								<div className="announcement-create__preview-label">
									Help → What’s new
								</div>
								<div className="announcement-create__changelog">
									<strong>{preview.title}</strong>
									<p>{preview.body}</p>
									<button type="button" className="announcement-create__cta">
										{preview.ctaLabel}
									</button>
									{preview.ctaDeepLink ? (
										<div className="announcement-create__link-hint">
											→ {preview.ctaDeepLink}
										</div>
									) : null}
								</div>
							</section>
						)}
					</Card>
				</Col>
			</Row>
		</ListPageShell>
	)
}
